/**
 * Package Limits Utility
 * 
 * Defines what features are allowed for each package in demo mode
 * 
 * Packages:
 * - Free: All features allowed but with quantity limits (1 worker, 1 partner, 20 products, 100 clients, 100 jobs)
 *   Note: SMS/Viber/Email sending is blocked for Free to prevent costs, but all other features are accessible
 * - Starter: Up to 1 worker, 1 partner, no Marketing, no Roba
 * - Standard: Up to 5 workers, 3 partners, Loyalty + Roba (magacin); messaging from Business+
 * - Business: Up to 20 workers, 10 partners, full Marketing (SMS/Viber/Email/WhatsApp)
 * - Premium: Unlimited workers/partners + Priority Support, Custom Database, Individual Website, AI features
 */

/**
 * Broj paketa iz Redux user objekta ili iz localStorage profila (npr. posle refresh-a ili starog zapisa bez package u result).
 * Podržava i string "2" iz JSON-a.
 */
const legacySubscriptionToPackageNumber = (sub) => {
  if (!sub || typeof sub !== 'string') return null;
  const s = sub.toLowerCase();
  if (s === 'premium') return 5;
  if (s === 'business') return 4;
  if (s === 'standard') return 3;
  if (s === 'starter') return 2;
  if (s === 'free') return 1;
  return null;
};

const resolvePackageNumber = (user) => {
  let pkg = user?.result?.package;
  if (pkg === undefined || pkg === null) {
    pkg = user?.package;
  }
  if (pkg === undefined || pkg === null) {
    try {
      if (typeof window !== 'undefined') {
        const raw = localStorage.getItem('profile');
        if (raw) {
          const profile = JSON.parse(raw);
          if (profile?.result?.package != null) {
            pkg = profile.result.package;
          }
          if ((pkg === undefined || pkg === null) && profile?.result?.subscriptionPackage != null) {
            pkg = legacySubscriptionToPackageNumber(profile.result.subscriptionPackage);
          }
        }
      }
    } catch {
      pkg = 1;
    }
  }
  if ((pkg === undefined || pkg === null) && user?.result?.subscriptionPackage != null) {
    pkg = legacySubscriptionToPackageNumber(user.result.subscriptionPackage);
  }
  const n = Number(pkg);
  if (!Number.isFinite(n) || n < 1 || n > 5) {
    return 1;
  }
  return n;
};

/**
 * Get current package for user (demo or real)
 */
export const getCurrentPackage = (user) => {
  const isDemoUser = user?.result?.email === 'demo@gmail.com';

  if (isDemoUser) {
    return localStorage.getItem('demoPackage') || 'Starter';
  }

  const packageNumber = resolvePackageNumber(user);
  const packageMap = {
    1: 'Free',
    2: 'Starter',
    3: 'Standard',
    4: 'Business',
    5: 'Premium',
  };
  return packageMap[packageNumber] || 'Free';
};

/**
 * Check if feature is allowed for current package
 * Free plan allows all features but with quantity limits
 */
export const isFeatureAllowed = (feature, user) => {
  const currentPackage = getCurrentPackage(user);
  
  // Business and Premium allow everything
  if (currentPackage === 'Business' || currentPackage === 'Premium') {
    return true;
  }
  
  // Free plan allows all features but with quantity limits
  if (currentPackage === 'Free') {
    // Only Premium-exclusive features are not allowed for Free
    switch (feature) {
      case 'ai':
      case 'voiceInput':
      case 'customDatabase':
      case 'individualWebsite':
      case 'prioritySupport':
        return false;
      default:
        return true; // All other features allowed for Free
    }
  }
  
  // Feature-specific checks for other packages
  switch (feature) {
    case 'marketing':
      // Marketing allowed for Standard, Business, Premium
      return ['Standard', 'Business', 'Premium'].includes(currentPackage);
    
    case 'roba':
      // Free ima u bloku iznad (default true); ovde Starter bez robe, Standard+ sa magacinom; webshop je poseban feature
      return ['Standard', 'Business', 'Premium'].includes(currentPackage);
    
    case 'workers':
      // Workers allowed for all, but with limits
      return true;
    
    case 'partners':
      // Partners allowed for Starter+, but with limits
      return ['Starter', 'Standard', 'Business', 'Premium'].includes(currentPackage);
    
    case 'webshop':
      // WebShop allowed for Free (with limits), Business and Premium only
      // Not allowed for Starter and Standard
      return ['Free', 'Business', 'Premium'].includes(currentPackage);
    
    case 'ai':
      // AI Biznis, AI Import, AI Export + ekstrakcija iz slika — od Starter paketa (mesečni limit slika)
      return ['Starter', 'Standard', 'Business', 'Premium'].includes(currentPackage);

    case 'voiceInput':
      // Glasovni unos u formi posla (Web Speech API) — od Standard paketa
      return ['Standard', 'Business', 'Premium'].includes(currentPackage);
    
    case 'customDatabase':
      // Custom database only for Premium
      return currentPackage === 'Premium';
    
    case 'individualWebsite':
      // Individual website only for Premium
      return currentPackage === 'Premium';
    
    case 'prioritySupport':
      // Priority support only for Premium
      return currentPackage === 'Premium';
    
    default:
      return true;
  }
};

/**
 * Get maximum number of workers allowed for package
 */
export const getMaxWorkers = (user) => {
  const currentPackage = getCurrentPackage(user);
  
  switch (currentPackage) {
    case 'Free':
      return 1; // 1 worker allowed in demo
    case 'Starter':
      return 1;
    case 'Standard':
      return 5;
    case 'Business':
      return 20;
    case 'Premium':
      return Infinity;
    default:
      return 0;
  }
};

/**
 * Get maximum number of partners allowed for package
 */
export const getMaxPartners = (user) => {
  const currentPackage = getCurrentPackage(user);
  
  switch (currentPackage) {
    case 'Free':
      return 1; // 1 partner allowed in demo
    case 'Starter':
      return 1;
    case 'Standard':
      return 3;
    case 'Business':
      return 10;
    case 'Premium':
      return Infinity;
    default:
      return 0;
  }
};

/**
 * Marketing kanali po paketu (SMS, Viber, Email, WhatsApp — samo Business i Premium).
 */
export const isMarketingChannelAllowed = (channel, user) => {
  if (!isFeatureAllowed('marketing', user)) {
    return false;
  }
  const currentPackage = getCurrentPackage(user);
  if (currentPackage === 'Standard' || currentPackage === 'Starter' || currentPackage === 'Free') {
    return false;
  }
  return ['email', 'sms', 'viber', 'whatsapp'].includes(channel);
};

/**
 * Check if user can add more workers
 */
export const canAddWorker = (user, currentWorkerCount) => {
  const maxWorkers = getMaxWorkers(user);
  return currentWorkerCount < maxWorkers;
};

/**
 * Check if user can add more partners
 */
export const canAddPartner = (user, currentPartnerCount) => {
  const maxPartners = getMaxPartners(user);
  return currentPartnerCount < maxPartners;
};

/**
 * Get maximum number of products allowed for package
 */
export const getMaxProducts = (user) => {
  const currentPackage = getCurrentPackage(user);
  
  switch (currentPackage) {
    case 'Free':
      return 20; // 20 products for Free
    case 'Starter':
    case 'Standard':
    case 'Business':
    case 'Premium':
      return Infinity; // Unlimited
    default:
      return 20;
  }
};

/**
 * Get maximum number of clients allowed for package
 */
export const getMaxClients = (user) => {
  const currentPackage = getCurrentPackage(user);
  
  switch (currentPackage) {
    case 'Free':
      return 100; // 100 clients for Free
    case 'Starter':
    case 'Standard':
    case 'Business':
    case 'Premium':
      return Infinity; // Unlimited
    default:
      return 100;
  }
};

/**
 * Get maximum number of jobs allowed for package
 */
export const getMaxJobs = (user) => {
  const currentPackage = getCurrentPackage(user);
  
  switch (currentPackage) {
    case 'Free':
      return 100; // 100 jobs for Free (until end of trial period)
    case 'Starter':
    case 'Standard':
    case 'Business':
    case 'Premium':
      return Infinity; // Unlimited
    default:
      return 100;
  }
};

/**
 * Get maximum number of device images per job for current package
 * - Standard (39€): up to 3 images
 * - Business (79€): up to 10 images
 * - Other packages: 0 (no images)
 */
export const getMaxJobImages = (user) => {
  const currentPackage = getCurrentPackage(user);

  switch (currentPackage) {
    case 'Standard':
      return 3;
    case 'Business':
      return 10;
    default:
      return 0;
  }
};

/**
 * Check if user can add more products
 */
export const canAddProduct = (user, currentProductCount) => {
  const maxProducts = getMaxProducts(user);
  return currentProductCount < maxProducts;
};

/**
 * Check if user can add more clients
 */
export const canAddClient = (user, currentClientCount) => {
  const maxClients = getMaxClients(user);
  return currentClientCount < maxClients;
};

/**
 * Check if user can add more jobs
 */
export const canAddJob = (user, currentJobCount) => {
  const maxJobs = getMaxJobs(user);
  return currentJobCount < maxJobs;
};

/**
 * Get package restrictions message
 */
export const getPackageRestrictionMessage = (feature, user) => {
  const currentPackage = getCurrentPackage(user);
  
  if (isFeatureAllowed(feature, user)) {
    return null;
  }
  
  const packageMap = {
    'marketing': 'Marketing funkcionalnosti su dostupne od Standard paketa',
    'roba': 'Roba (magacin i inventar) je dostupna od Standard paketa; Web shop od Business paketa',
    'partners': 'Partneri su dostupni od Starter paketa',
    'webshop': 'Web Shop je dostupan od Business paketa',
    'ai': 'AI funkcionalnosti (AI Biznis, AI Import, AI Export, ekstrakcija iz slika) dostupne su od Starter paketa uz mesečni limit slika',
    'voiceInput': 'Glasovni unos podataka dostupan je od Standard paketa',
    'customDatabase': 'Custom baze su dostupne u Premium paketu',
    'individualWebsite': 'Individualne web stranice su dostupne u Premium paketu',
    'prioritySupport': 'Prioritetna podrška je dostupna u Premium paketu'
  };
  
  return packageMap[feature] || `Ova funkcionalnost nije dostupna u ${currentPackage} paketu`;
};
